// src/products/dataroom/api/embedService.js
import { supabase } from '../../../config';

const embedService = {
  // Check if user can embed/merge projects
  async canManageProjects(sourceProjectId, targetProjectId, userId) {
    try {
      // Check user has creator access to both projects
      const { data: sourceAccess } = await supabase
        .from('dataroom_users')
        .select('role')
        .eq('project_id', sourceProjectId)
        .eq('user_id', userId)
        .single();

      const { data: targetAccess } = await supabase
        .from('dataroom_users')
        .select('role')
        .eq('project_id', targetProjectId)
        .eq('user_id', userId)
        .single();

      return sourceAccess?.role === 'creator' && targetAccess?.role === 'creator';
    } catch (error) {
      console.error('Error checking project permissions:', error);
      return false;
    }
  },

  // Get list of projects user can embed (excluding current project)
  async getEmbeddableProjects(currentProjectId, userId) {
    try {
      const { data: projects, error } = await supabase
        .from('dataroom_projects')
        .select(`
          id,
          project_name,
          description,
          created_at,
          dataroom_users!inner(role)
        `)
        .eq('dataroom_users.user_id', userId)
        .eq('dataroom_users.role', 'creator')
        .neq('id', currentProjectId)
        .order('project_name');

      if (error) throw error;
      return projects || [];
    } catch (error) {
      console.error('Error fetching embeddable projects:', error);
      throw error;
    }
  },

  // Embed one dataroom into another
  async embedDataroom(parentProjectId, embeddedProjectId, folderName, userId) {
    try {
      // 1. Check permissions
      const canEmbed = await this.canManageProjects(embeddedProjectId, parentProjectId, userId);
      if (!canEmbed) {
        throw new Error('Insufficient permissions to embed dataroom');
      }

      // 2. Check if already embedded
      const { data: existingEmbed } = await supabase
        .from('dataroom_embeds')
        .select('id')
        .eq('parent_project_id', parentProjectId)
        .eq('embedded_project_id', embeddedProjectId)
        .eq('is_active', true)
        .single();

      if (existingEmbed) {
        throw new Error('This dataroom is already embedded in the current project');
      }

      // 3. Create embed record
      const { data: embedRecord, error: embedError } = await supabase
        .from('dataroom_embeds')
        .insert({
          parent_project_id: parentProjectId,
          embedded_project_id: embeddedProjectId,
          embedded_as_folder_name: folderName,
          created_by: userId
        })
        .select()
        .single();

      if (embedError) throw embedError;

      // 4. Create virtual folder in parent dataroom
      const { data: virtualFolder, error: folderError } = await supabase
        .from('dataroom_items')
        .insert({
          project_id: parentProjectId,
          name: folderName,
          type: 'folder',
          is_folder: true,
          is_embedded_folder: true,
          embedded_project_id: embeddedProjectId,
          embed_id: embedRecord.id,
          created_by: userId
        })
        .select()
        .single();

      if (folderError) throw folderError;

      // 5. Log the action
      await supabase
        .from('dataroom_logs')
        .insert({
          user_id: userId,
          action: 'embed_dataroom',
          project_id: parentProjectId,
          details: {
            embedded_project_id: embeddedProjectId,
            folder_name: folderName,
            embed_id: embedRecord.id
          }
        });

      return { embedRecord, virtualFolder };
    } catch (error) {
      console.error('Embed error:', error);
      throw error;
    }
  },

  // Merge one dataroom into another
  async mergeDataroom(sourceProjectId, targetProjectId, userId) {
    try {
      // 1. Check permissions
      const canMerge = await this.canManageProjects(sourceProjectId, targetProjectId, userId);
      if (!canMerge) {
        throw new Error('Insufficient permissions to merge dataroom');
      }

      // 2. Get source project name
      const { data: sourceProject } = await supabase
        .from('dataroom_projects')
        .select('project_name')
        .eq('id', sourceProjectId)
        .single();

      if (!sourceProject) {
        throw new Error('Source dataroom not found');
      }

      // 3. Get all items from source dataroom
      const { data: sourceItems, error: fetchError } = await supabase
        .from('dataroom_items')
        .select('*')
        .eq('project_id', sourceProjectId)
        .eq('is_embedded_folder', false) // Don't merge embedded folders
        .order('created_at');

      if (fetchError) throw fetchError;

      if (!sourceItems || sourceItems.length === 0) {
        throw new Error('Source dataroom is empty');
      }

      // 4. Create folder for merged content
      const mergeFolderName = `Merged from ${sourceProject.project_name}`;
      const { data: mergeFolder, error: mergeFolderError } = await supabase
        .from('dataroom_items')
        .insert({
          project_id: targetProjectId,
          name: mergeFolderName,
          type: 'folder',
          is_folder: true,
          created_by: userId
        })
        .select()
        .single();

      if (mergeFolderError || !mergeFolder) {
        throw new Error('Failed to create merge folder');
      }

      // 5. Copy all items with new IDs and parent relationships
      const itemMapping = new Map(); // old_id -> new_id
      let itemsCopied = 0;

      // Sort items to process parents before children
      const sortedItems = this.sortItemsByHierarchy(sourceItems);

      // Copy each item
      for (const item of sortedItems) {
        const newParentId = item.parent_id === null 
          ? mergeFolder.id 
          : itemMapping.get(item.parent_id) || mergeFolder.id;

        const newItem = {
          project_id: targetProjectId,
          name: item.name,
          type: item.type,
          is_folder: item.is_folder,
          file_path: item.file_path,
          file_size: item.file_size,
          file_type: item.file_type,
          file_url: item.file_url,
          parent_id: newParentId,
          created_by: userId
        };

        const { data: copiedItem, error: copyError } = await supabase
          .from('dataroom_items')
          .insert(newItem)
          .select()
          .single();

        if (copyError) {
          console.error('Error copying item:', item.name, copyError);
          continue; // Skip this item but continue with others
        }

        itemMapping.set(item.id, copiedItem.id);
        itemsCopied++;
      }

      // 6. Record merge history
      const { data: mergeRecord } = await supabase
        .from('dataroom_merges')
        .insert({
          source_project_id: sourceProjectId,
          target_project_id: targetProjectId,
          merged_by: userId,
          items_copied: itemsCopied,
          merge_details: {
            merge_folder_id: mergeFolder.id,
            merge_folder_name: mergeFolderName,
            source_project_name: sourceProject.project_name
          }
        })
        .select()
        .single();

      // 7. Log the action
      await supabase
        .from('dataroom_logs')
        .insert({
          user_id: userId,
          action: 'merge_dataroom',
          project_id: targetProjectId,
          details: {
            source_project_id: sourceProjectId,
            items_copied: itemsCopied,
            merge_folder_id: mergeFolder.id
          }
        });

      return { 
        mergeRecord, 
        itemsCopied, 
        mergeFolderId: mergeFolder.id,
        mergeFolderName 
      };
    } catch (error) {
      console.error('Merge error:', error);
      throw error;
    }
  },

  // Helper function to sort items by hierarchy (parents first)
  sortItemsByHierarchy(items) {
    const sorted = [];
    const processed = new Set();
    
    const processItem = (item) => {
      if (processed.has(item.id)) return;
      
      // If item has a parent, make sure parent is processed first
      if (item.parent_id !== null) {
        const parent = items.find(i => i.id === item.parent_id);
        if (parent && !processed.has(parent.id)) {
          processItem(parent);
        }
      }
      
      sorted.push(item);
      processed.add(item.id);
    };

    // Process all root items first, then their children
    items.forEach(item => {
      if (!processed.has(item.id)) {
        processItem(item);
      }
    });

    return sorted;
  },

  // Get embedded content for a virtual folder
  async getEmbeddedContent(embedId) {
    try {
      // Get embed details
      const { data: embed, error: embedError } = await supabase
        .from('dataroom_embeds')
        .select(`
          *,
          dataroom_projects!embedded_project_id(project_name, description)
        `)
        .eq('id', embedId)
        .eq('is_active', true)
        .single();

      if (embedError || !embed) {
        console.error('Embed not found:', embedError);
        return null;
      }

      // Get items from embedded project (only non-embedded items to avoid loops)
      const { data: items, error: itemsError } = await supabase
        .from('dataroom_items')
        .select('*')
        .eq('project_id', embed.embedded_project_id)
        .eq('is_embedded_folder', false)
        .order('name');

      if (itemsError) {
        console.error('Error fetching embedded items:', itemsError);
        return { embed, items: [] };
      }

      return { embed, items: items || [] };
    } catch (error) {
      console.error('Get embedded content error:', error);
      throw error;
    }
  },

  // Remove embed
  async removeEmbed(embedId, userId) {
    try {
      // Check if user created the embed or has access to parent project
      const { data: embed } = await supabase
        .from('dataroom_embeds')
        .select(`
          *,
          dataroom_users!inner(role)
        `)
        .eq('id', embedId)
        .eq('dataroom_users.project_id', 'parent_project_id')
        .eq('dataroom_users.user_id', userId)
        .eq('dataroom_users.role', 'creator')
        .single();

      if (!embed) {
        throw new Error('Embed not found or insufficient permissions');
      }

      // Remove virtual folder
      await supabase
        .from('dataroom_items')
        .delete()
        .eq('embed_id', embedId);

      // Mark embed as inactive
      await supabase
        .from('dataroom_embeds')
        .update({ is_active: false })
        .eq('id', embedId);

      // Log the action
      await supabase
        .from('dataroom_logs')
        .insert({
          user_id: userId,
          action: 'remove_embed',
          project_id: embed.parent_project_id,
          details: {
            embed_id: embedId,
            embedded_project_id: embed.embedded_project_id
          }
        });

      return true;
    } catch (error) {
      console.error('Remove embed error:', error);
      throw error;
    }
  },

  // Get merge history for a project
  async getMergeHistory(projectId) {
    try {
      const { data: merges, error } = await supabase
        .from('dataroom_merges')
        .select(`
          *,
          dataroom_projects!source_project_id(project_name),
          user_profiles!merged_by(first_name, last_name, email)
        `)
        .eq('target_project_id', projectId)
        .order('merged_at', { ascending: false });

      if (error) throw error;
      return merges || [];
    } catch (error) {
      console.error('Error fetching merge history:', error);
      throw error;
    }
  }
};

export default embedService;